import { Box, Stack, Typography } from '@mui/material';
import { logger } from '../../services/logger/logger';
import { ColDef } from 'ag-grid-community';
import dayjs from 'dayjs';
import { useEffect, useMemo, useState } from 'react';
import Button from '../../components/common/Button';
import ClientSideAgGrid from '../../components/common/customAgGrid/ClientSideAgGrid';
import CustomActionButton from '../../components/common/customAgGrid/CustomActionButton';
import CustomAgMultiSelectFilter from '../../components/common/customAgGrid/CustomAgMultiSelectFilter';
import Dropdown from '../../components/common/Dropdown';
import InputField from '../../components/common/InputField';
import MasterCatalogueTable from '../../components/common/MasterCatalogueTable';
import Modal from '../../components/common/Modal';
import PageHeader from '../../components/PageHeader';
import { useGetCompaniesMakersListQuery } from '../../store/apiSlices/companies/companiesMakers/companiesMakersApis';
import { useGetLubricantCategoriesListQuery } from '../../store/apiSlices/lubricants/lubricantCategory/lubricantCategoryApis';
import { useGetLubricantGroupsListQuery } from '../../store/apiSlices/lubricants/lubricantGroups/lubricantGroupsApis';
import {
  useGetLubricantProductsQuery,
  useGetUnitsOfMeasurementQuery,
} from '../../store/apiSlices/lubricants/lubricantProducts/lubricantProductsApis';
import { useGetLubricantTypesListQuery } from '../../store/apiSlices/lubricants/lubricantTypes/lubricantTypesApis';
import {
  useCreateTypeInProductMutation,
  useGetTypeInProductsListQuery,
} from '../../store/apiSlices/typeInProducts/typeInProductsApis';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { canEditTypeInProducts } from '../../utils/permissions';
import { showErrorToast, showSuccessToast } from '../../components/common/BaseToaster';
import { TableCardContent } from '../../components/common/styled/TableCardContent';

const TypeInProductsContainer = () => {
  const { t } = useTranslation();
  const userInfo = useSelector((state: any) => state.user.userInfo);
  const canEdit = canEditTypeInProducts(userInfo?.authorityName);
  const [openModal, setOpenModal] = useState(false);
  const [type, setType] = useState<string>('');
  const [selectedProduct, setSelectedProduct] = useState<number | null>(null);
  const [selectedTypeInProductId, setSelectedTypeInProductId] = useState<number | null>(null);
  const [selectedMakerId, setSelectedMakerId] = useState<number | null>(null);
  const [openConvertModal, setOpenConvertModal] = useState(false);
  const [convertFormData, setConvertFormData] = useState({
    productName: '',
    maker: '',
    category: '',
    type: '',
    group: '',
    unit: '',
    status: 'Active',
  });
  const [isFilterActive, setIsFilterActive] = useState(false);
  const [resetFiltersSignal, setResetFiltersSignal] = useState(false);
  const [tableColumns, setTableColumns] = useState<ColDef[]>([]);

  const {
    data: typeInProductsList,
    isLoading: typeInProductsListLoading,
    refetch: refetchTypeInProducts,
  } = useGetTypeInProductsListQuery(undefined, {
    refetchOnMountOrArgChange: true,
    refetchOnFocus: true,
    refetchOnReconnect: true,
  });
  const [createTypeInProduct] = useCreateTypeInProductMutation();

  // Master catalogue API
  const {
    data: masterCatalogueData = [],
    isLoading: isLoadingMasterCatalogue,
    refetch: refetchMasterCatalogue,
  } = useGetLubricantProductsQuery(
    { active: true, ...(selectedMakerId && { makerId: selectedMakerId }) },
    {
      skip: !openModal || type !== 'Merge',
      refetchOnMountOrArgChange: true,
      refetchOnFocus: true,
      refetchOnReconnect: true,
    },
  );

  // Refetch master catalogue API when merge modal opens
  useEffect(() => {
    if (openModal && type === 'Merge') {
      refetchMasterCatalogue();
    }
  }, [openModal, type, refetchMasterCatalogue]);

  // Dropdown data APIs for convert modal
  const { data: makersData } = useGetCompaniesMakersListQuery({});
  const { data: categoriesData } = useGetLubricantCategoriesListQuery({});
  const { data: typesData } = useGetLubricantTypesListQuery({});
  const { data: groupsData } = useGetLubricantGroupsListQuery({});
  const { data: unitsData } = useGetUnitsOfMeasurementQuery();

  // Transform master catalogue data
  const masterCatalogueList = useMemo(() => {
    if (
      !masterCatalogueData ||
      Array.isArray(masterCatalogueData) ||
      !masterCatalogueData.data?.content
    ) {
      return [];
    }

    return masterCatalogueData?.data?.content?.map((product: any) => ({
      id: product.id,
      productName: product.name,
      maker: product.makerName || '-',
      category: product.categoryName || '-',
      type: product.typeName || '-',
      unit: product.symbol || '-',
      status: product.active ? 'Active' : 'Inactive',
      lastUpdatedBy: product.updatedBy?.toString() || '-',
      lastUpdatedOn: dayjs(product.updatedOn).format('DD/MM/YYYY HH:mm'),
    }));
  }, [masterCatalogueData]);

  const handleProductSelect = (productId: number) => {
    // Toggle selection: if clicking the same product, uncheck it
    setSelectedProduct((prevSelected) => (prevSelected === productId ? null : productId));
  };

  // Transform dropdown data for convert modal
  const convertModalData = useMemo(() => {
    return {
      inactiveProducts: [],
      masterCatalogue: [],
      makers: makersData || [],
      categories: categoriesData?.data || [],
      types: typesData?.data || [],
      groups: groupsData?.data || [],
      units: unitsData?.data || [],
    };
  }, [makersData, categoriesData, typesData, groupsData, unitsData]);

  const handleConvertFieldChange = (field: string, value: string) => {
    setConvertFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const isConvertFormComplete = useMemo(() => {
    return (
      convertFormData.productName.trim() !== '' &&
      convertFormData.maker !== '' &&
      convertFormData.category !== '' &&
      convertFormData.type !== '' &&
      convertFormData.unit !== ''
    );
  }, [convertFormData]);

  const breadcrumbOptions = [
    {
      label: t('typeInProducts.title'),
      isActive: true,
      onClick: () => {},
    },
  ];

  const tableMenuOptions = useMemo(
    () => [
      {
        label: t('buttons.merge'),
        onClick: (rowData?: any) => {
          setOpenModal(true);
          setType('Merge');
          setSelectedProduct(null); // Reset selection when opening merge modal
          setSelectedTypeInProductId(rowData?.id || null); // Store the type-in product ID
          setSelectedMakerId(rowData?.makerId || null); // Store the maker ID for filtering
        },
      },
      {
        label: t('buttons.convert'),
        onClick: (rowData?: any) => {
          setOpenConvertModal(true);
          setSelectedTypeInProductId(rowData?.id || null); // Store the type-in product ID
          setConvertFormData({
            productName: rowData?.productName || '',
            maker: rowData?.maker && rowData.maker !== '-' ? rowData.maker : '',
            category: rowData?.category || '',
            type: rowData?.type && rowData.type !== '-' ? rowData.type : '',
            group: '', // Group is not in the current data
            unit: rowData?.symbol || '', // Use symbol from backend (e.g., "C/kg", "L", etc.)
            status: 'Active', // Default to Active
          });
        },
      },
    ],
    [t],
  );

  const columns: ColDef[] = useMemo(
    () => [
      {
        field: 'productName',
        headerName: t('table.columns.productName'),
        sortable: true,
        width: 200,
        filter: 'agSetColumnFilter',
        floatingFilter: true,
        floatingFilterComponent: CustomAgMultiSelectFilter,
      },
      {
        field: 'maker',
        headerName: t('table.columns.maker'),
        sortable: true,
        width: 200,
        filter: 'agSetColumnFilter',
        floatingFilter: true,
        floatingFilterComponent: CustomAgMultiSelectFilter,
      },
      {
        field: 'vesselName',
        headerName: t('table.columns.vessel'),
        sortable: true,
        width: 200,
        filter: 'agSetColumnFilter',
        floatingFilter: true,
        floatingFilterComponent: CustomAgMultiSelectFilter,
      },
      {
        field: 'category',
        headerName: t('table.columns.category'),
        sortable: true,
        width: 200,
        filter: 'agSetColumnFilter',
        floatingFilter: true,
        floatingFilterComponent: CustomAgMultiSelectFilter,
      },
      {
        field: 'type',
        headerName: t('table.columns.type'),
        sortable: true,
        width: 150,
        filter: 'agSetColumnFilter',
        floatingFilter: true,
        floatingFilterComponent: CustomAgMultiSelectFilter,
      },
      {
        field: 'unit',
        headerName: t('table.columns.unit'),
        sortable: true,
        width: 150,
        filter: 'agSetColumnFilter',
        floatingFilter: true,
        floatingFilterComponent: CustomAgMultiSelectFilter,
      },
    ],
    [t],
  );

  useEffect(() => {
    if (canEdit) {
      const actionsColumn: ColDef = {
        field: 'actions',
        headerName: '',
        width: 50,
        maxWidth: 50,
        flex: 0,
        sortable: false,
        pinned: 'right',
        cellRenderer: (params: any) => (
          <CustomActionButton menuOptions={tableMenuOptions} rowData={params.data} />
        ),
      };
      setTableColumns([...columns, actionsColumn]);
    } else {
      setTableColumns(columns);
    }
  }, [canEdit, columns, tableMenuOptions]);

  const handleTypeInProduct = async (type: string) => {
    try {
      if (type === 'Merge') {
        const payload = {
          typeInProductId: selectedTypeInProductId,
          targetProductId: selectedProduct,
          actionType: 'MERGE',
          // updatedBy: 0,
        };
        const res: any = await createTypeInProduct(payload).unwrap();
        showSuccessToast(res?.message);

        // Refetch type-in products list to update the table
        await refetchTypeInProducts();

        // Close modal and reset state on successful merge
        setOpenModal(false);
        setSelectedProduct(null);
        setSelectedTypeInProductId(null);
        setSelectedMakerId(null);
      } else {
        const payload = {
          typeInProductId: 0,
          targetProductId: 0,
          actionType: 'CONVERT',
          // updatedBy: 0,
        };
        const res: any = await createTypeInProduct(payload).unwrap();
        showSuccessToast(res?.message);
      }
    } catch (error: any) {
      logger.error('Failed to merge type-in product', error as Error);
    }
  };

  const handleConvertSubmit = async () => {
    try {
      if (!selectedTypeInProductId) {
        showErrorToast(t('toast.validationError'), t('typeInProducts.typeInProductIdMissing'));
        return;
      }

      // Map display values to IDs
      const selectedMaker = convertModalData.makers.find(
        (maker: any) => maker.shortName === convertFormData.maker,
      );
      const selectedCategory = convertModalData.categories.find(
        (category: any) => category.categoryName === convertFormData.category,
      );
      const selectedType = convertModalData.types.find(
        (type: any) => type.name === convertFormData.type,
      );
      const selectedUnit = convertModalData.units.find(
        (unit: any) => unit.symbol === convertFormData.unit,
      );
      const selectedGroup = convertModalData.groups?.find(
        (group: any) => group.groupName === convertFormData.group,
      );

      if (!selectedMaker || !selectedCategory || !selectedGroup || !selectedType || !selectedUnit) {
        showErrorToast(
          t('toast.validationError'),
          t('typeInProducts.selectValidValuesForAllFields'),
        );
        return;
      }

      const payload = {
        typeInProductId: selectedTypeInProductId,
        actionType: 'CONVERT',
        productName: convertFormData.productName.trim(),
        makerId: Number(selectedMaker.id),
        categoryId: Number(selectedCategory.id),
        typeId: Number(selectedType.id),
        productGroupId: selectedGroup.id,
        uomId: Number(selectedUnit.id),
        status: convertFormData.status === 'Active',
        // updatedBy: 0, // TODO: Replace with actual user ID from auth context
      };

      await createTypeInProduct(payload).unwrap();

      const { helpingMethod } = await import('../../utils/HelpingMethod');
      helpingMethod.showToaster(t('typeInProducts.convertSuccess'), 'success');

      // Refetch type-in products list to update the table
      await refetchTypeInProducts();

      setOpenConvertModal(false);
      setSelectedTypeInProductId(null);
      setConvertFormData({
        productName: '',
        maker: '',
        category: '',
        type: '',
        group: '',
        unit: '',
        status: 'Active',
      });
    } catch (error: any) {
      logger.error('Failed to convert type-in product', error as Error);
    }
  };

  const handleConvertClose = () => {
    setOpenConvertModal(false);
    setSelectedTypeInProductId(null);
    setConvertFormData({
      productName: '',
      maker: '',
      category: '',
      type: '',
      group: '',
      unit: '',
      status: 'Active',
    });
  };

  return (
    <Stack gap={1} sx={{ height: '100%', minHeight: 0 }}>
      <PageHeader
        options={breadcrumbOptions}
        isFilterActive={isFilterActive}
        resetFilters={() => setResetFiltersSignal(true)}
      />
      <TableCardContent>
        <ClientSideAgGrid
          rowData={typeInProductsList ?? []}
          columnDefs={tableColumns as any}
          loading={typeInProductsListLoading}
          className="type-in-products-grid"
          onFiltersActiveChange={(active: boolean) => {
            setIsFilterActive(active);
            setResetFiltersSignal(false);
          }}
          resetFiltersSignal={resetFiltersSignal}
        />
      </TableCardContent>

      <Modal
        open={openModal}
        onClose={() => {
          setOpenModal(false);
          setSelectedProduct(null);
          setSelectedTypeInProductId(null);
          setSelectedMakerId(null);
        }}
        title={
          type === 'Merge' ? t('typeInProducts.mergeProduct') : t('typeInProducts.convertProduct')
        }
        size={type === 'Merge' ? 'lg' : 'xs'}
        contentSx={{ display: 'flex', flexDirection: 'column' }}
        footer={
          <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
            <Button
              variant="red"
              onClick={() => {
                setOpenModal(false);
                setSelectedProduct(null);
                setSelectedTypeInProductId(null);
                setSelectedMakerId(null);
              }}
            >
              {t('buttons.cancel')}
            </Button>
            <Button variant="outlinedBlue" onClick={() => handleTypeInProduct(type)}>
              {type === 'Merge' ? t('buttons.merge') : t('buttons.convert')}
            </Button>
          </Box>
        }
      >
        {type === 'Merge' ? (
          <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
            <Box sx={{ mt: 2, mb: 2, ml: 1 }}>
              <Typography sx={{ fontWeight: 500, fontSize: '16px', mb: 1 }}>
                {t('typeInProducts.masterCatalogueProducts')}
              </Typography>
              <Typography sx={{ fontWeight: 400, fontSize: '15px' }}>
                {t('typeInProducts.mergeDescription')}
              </Typography>
            </Box>
            <MasterCatalogueTable
              data={masterCatalogueList}
              loading={isLoadingMasterCatalogue}
              selectedProduct={selectedProduct}
              onProductSelect={handleProductSelect}
            />
          </Box>
        ) : (
          <Box>
            <Typography>
              {type === 'Merge'
                ? t('typeInProducts.mergeProduct')
                : t('typeInProducts.convertProduct')}
            </Typography>
          </Box>
        )}
      </Modal>

      {/* Convert Modal - Custom Type-in Product Modal */}
      <Modal
        open={openConvertModal}
        onClose={handleConvertClose}
        title={t('typeInProducts.convertToOfficial')}
        size="vertical"
        isTypeInModal={true}
        footer={
          <>
            <Button variant="red" sx={{ fontSize: '14px' }} onClick={handleConvertClose}>
              {t('buttons.cancel')}
            </Button>
            {isConvertFormComplete ? (
              <Button
                variant="outlinedBlue"
                sx={{ fontSize: '14px' }}
                onClick={handleConvertSubmit}
              >
                {t('typeInProducts.convertToOfficial')}
              </Button>
            ) : (
              <Button
                variant="outlinedBlue"
                sx={{ fontSize: '14px' }}
                onClick={handleConvertSubmit}
                disabled
              >
                {t('form.fillAllFields')}
              </Button>
            )}
          </>
        }
      >
        <Box>
          <Box sx={{ mt: 4, mb: 3, ml: 1 }}>
            <Typography
              sx={{
                fontWeight: 500,
                fontSize: '16px',
                mb: 1,
              }}
            >
              {t('typeInProducts.typeInNewProduct')}
            </Typography>
            <Typography
              sx={{
                fontWeight: 400,
                fontSize: '15px',
              }}
            >
              {t('typeInProducts.convertDescription')}
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, ml: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Typography
                sx={{
                  width: '120px',
                  fontSize: '14px',
                  fontWeight: 500,
                }}
              >
                {t('form.productName')}
              </Typography>
              <InputField
                value={convertFormData.productName}
                onChange={(e) => handleConvertFieldChange('productName', e.target.value)}
                sx={{ flex: 1 }}
              />
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Typography
                sx={{
                  width: '120px',
                  fontSize: '14px',
                  fontWeight: 500,
                }}
              >
                {t('form.maker')}
              </Typography>
              <Dropdown
                value={convertFormData.maker}
                onChange={(e) => handleConvertFieldChange('maker', e.target.value)}
                options={
                  convertModalData?.makers?.map((maker: any) => ({
                    value: maker.shortName,
                    label: maker.shortName,
                  })) || []
                }
                sx={{ flex: 1 }}
                disabled={true}
              />
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Typography
                sx={{
                  width: '120px',
                  fontSize: '14px',
                  fontWeight: 500,
                }}
              >
                {t('form.category')}
              </Typography>
              <Dropdown
                value={convertFormData.category}
                onChange={(e) => handleConvertFieldChange('category', e.target.value)}
                options={
                  convertModalData?.categories?.map((category: any) => ({
                    value: category.categoryName,
                    label: category.categoryName,
                  })) || []
                }
                sx={{ flex: 1 }}
              />
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Typography
                sx={{
                  width: '120px',
                  fontSize: '14px',
                  fontWeight: 500,
                }}
              >
                {t('form.type')}
              </Typography>
              <Dropdown
                value={convertFormData.type}
                onChange={(e) => handleConvertFieldChange('type', e.target.value)}
                options={
                  convertModalData?.types?.map((type: any) => ({
                    value: type.name,
                    label: type.name,
                  })) || []
                }
                sx={{ flex: 1 }}
              />
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Typography
                sx={{
                  width: '120px',
                  fontSize: '14px',
                  fontWeight: 500,
                }}
              >
                {t('form.group')}
              </Typography>
              <Dropdown
                value={convertFormData.group || ''}
                onChange={(e) => handleConvertFieldChange('group', e.target.value)}
                options={
                  convertModalData?.groups?.map((group: any) => ({
                    value: group.groupName,
                    label: group.groupName,
                  })) || []
                }
                sx={{ flex: 1 }}
              />
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Typography
                sx={{
                  width: '120px',
                  fontSize: '14px',
                  fontWeight: 500,
                }}
              >
                {t('form.unit')}
              </Typography>
              <Dropdown
                value={convertFormData.unit}
                onChange={(e) => handleConvertFieldChange('unit', e.target.value)}
                options={
                  convertModalData?.units
                    ?.map((unit: any) => ({
                      value: unit.symbol,
                      label: unit.symbol,
                    }))
                    ?.filter((unit: any) => ['kg', 'L']?.includes(unit.label)) || []
                }
                sx={{ flex: 1 }}
              />
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Typography
                sx={{
                  width: '120px',
                  fontSize: '14px',
                  fontWeight: 500,
                }}
              >
                {t('form.status')}
              </Typography>
              <Dropdown
                value={convertFormData.status}
                onChange={(e) => handleConvertFieldChange('status', e.target.value)}
                options={[{ value: 'Active', label: t('common.active') }]}
                sx={{ flex: 1 }}
              />
            </Box>
          </Box>
        </Box>
      </Modal>
    </Stack>
  );
};

export default TypeInProductsContainer;
